import { useEffect, useRef, useCallback } from 'react';
import { App } from '@capacitor/app';
import { Platform } from '../utils/platform';
import { hardReload } from '../utils/hardReload';

const BACKGROUND_TIME_KEY = 'app_background_time';
const CONSECUTIVE_FAILURES_KEY = 'network_consecutive_failures';
const LAST_FAILURE_TIME_KEY = 'network_last_failure_time';

// Configuration
const LONG_BACKGROUND_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours - reload after this long in background
const MAX_CONSECUTIVE_FAILURES = 5; // Reload after this many consecutive failures
const FAILURE_WINDOW_MS = 30000; // 30 seconds - failures must be within this window to count

/**
 * Hook to detect and recover from broken WebView network state on iOS.
 * 
 * After very long background periods (overnight), iOS may terminate the WebView
 * process. When the app resumes, the WebView restarts but its network stack
 * can be in a corrupted state where all fetch requests fail with CORS-like errors.
 * 
 * This hook provides two recovery mechanisms:
 * 1. Proactive: Reload the page if backgrounded for more than 4 hours
 * 2. Reactive: Reload if too many consecutive network failures occur
 * 
 * Usage:
 * - Call useNetworkRecovery() in your App component
 * - Call recordNetworkFailure() when a fetch fails with a network error
 * - Call recordNetworkSuccess() when a fetch succeeds
 */

let failureRecorder: (() => void) | null = null;
let successRecorder: (() => void) | null = null;

export const recordNetworkFailure = () => {
  if (failureRecorder) failureRecorder();
};

export const recordNetworkSuccess = () => {
  if (successRecorder) successRecorder();
};

export const useNetworkRecovery = () => {
  const hasCheckedRef = useRef(false);
  
  // Record a network failure
  const recordFailure = useCallback(() => {
    if (!Platform.isNative) return;
    
    const now = Date.now();
    const lastFailureTime = parseInt(localStorage.getItem(LAST_FAILURE_TIME_KEY) || '0', 10);
    let consecutiveFailures = parseInt(localStorage.getItem(CONSECUTIVE_FAILURES_KEY) || '0', 10);
    
    // Reset counter if last failure was too long ago
    if (now - lastFailureTime > FAILURE_WINDOW_MS) {
      consecutiveFailures = 0;
    }
    
    consecutiveFailures++;
    localStorage.setItem(CONSECUTIVE_FAILURES_KEY, consecutiveFailures.toString());
    localStorage.setItem(LAST_FAILURE_TIME_KEY, now.toString());
    
    console.log(`[NetworkRecovery] Recorded failure #${consecutiveFailures}`);
    
    // If too many consecutive failures, reload the page to reset WebView
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      console.log('[NetworkRecovery] Too many consecutive failures, reloading to reset WebView...');
      localStorage.removeItem(CONSECUTIVE_FAILURES_KEY);
      localStorage.removeItem(LAST_FAILURE_TIME_KEY);
      
      // Small delay to allow log to be written
      setTimeout(() => {
        hardReload();
      }, 100);
    }
  }, []);
  
  // Record a network success - clears the failure counter
  const recordSuccess = useCallback(() => {
    if (!Platform.isNative) return;
    
    const hadFailures = localStorage.getItem(CONSECUTIVE_FAILURES_KEY);
    if (hadFailures && parseInt(hadFailures, 10) > 0) {
      console.log('[NetworkRecovery] Network recovered, clearing failure counter');
      localStorage.removeItem(CONSECUTIVE_FAILURES_KEY);
      localStorage.removeItem(LAST_FAILURE_TIME_KEY);
    }
  }, []);
  
  // Expose recorders globally
  useEffect(() => {
    failureRecorder = recordFailure;
    successRecorder = recordSuccess;
    return () => {
      failureRecorder = null;
      successRecorder = null;
    };
  }, [recordFailure, recordSuccess]);
  
  // Check for very long background on cold start
  useEffect(() => {
    if (!Platform.isNative || hasCheckedRef.current) {
      return;
    }
    
    hasCheckedRef.current = true;
    
    const storedTime = localStorage.getItem(BACKGROUND_TIME_KEY);
    if (storedTime) {
      const backgroundTime = parseInt(storedTime, 10);
      const elapsed = Date.now() - backgroundTime;
      
      // If backgrounded for very long time, reload immediately
      if (elapsed >= LONG_BACKGROUND_THRESHOLD_MS) {
        console.log(`[NetworkRecovery] App was backgrounded for ${Math.round(elapsed / 1000 / 60)} minutes, reloading to reset WebView...`);
        localStorage.removeItem(BACKGROUND_TIME_KEY);
        
        // Small delay to allow log to be written
        setTimeout(() => {
          hardReload();
        }, 100);
        return;
      }
    }
  }, []);
  
  // Listen for app state changes
  useEffect(() => {
    if (!Platform.isNative) {
      return;
    }
    
    const setupListener = async () => {
      try {
        const listener = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            // App going to background - persist time
            localStorage.setItem(BACKGROUND_TIME_KEY, Date.now().toString());
          } else {
            // App becoming active - check if backgrounded for very long
            const storedTime = localStorage.getItem(BACKGROUND_TIME_KEY);
            if (storedTime) {
              const backgroundTime = parseInt(storedTime, 10);
              const elapsed = Date.now() - backgroundTime;
              
              localStorage.removeItem(BACKGROUND_TIME_KEY);
              
              if (elapsed >= LONG_BACKGROUND_THRESHOLD_MS) {
                console.log(`[NetworkRecovery] App was backgrounded for ${Math.round(elapsed / 1000 / 60)} minutes, reloading to reset WebView...`);
                setTimeout(() => {
                  hardReload();
                }, 100);
              }
            }
          }
        });
        
        return () => {
          listener.remove();
        };
      } catch (error) {
        console.error('[NetworkRecovery] Failed to set up app state listener:', error);
      }
    };
    
    const cleanup = setupListener();
    
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, []);
};

export default useNetworkRecovery;
